<template>
  <div class="sticky top-0 lg:relative lg:top-auto" :class="mobileMenuOpen ? 'z-[100]' : 'z-[70]'">
    <!-- Main Navigation Bar -->
    <nav class="bg-white shadow-sm">
      <div class="lg:container mx-auto xl:px-32 sm:px-4 px-2">
        <div class="flex justify-between items-center transition-all duration-200 my-0 py-1.5 lg:my-2 lg:py-2" :class="isCompact ? 'lg:my-0 lg:py-1.5' : 'lg:my-2 lg:py-2'">

          <div class="logo-container flex items-center gap-1.5">
            <router-link to="/" class="sm:text-xl text-lg font-bold text-blue-800">properflight</router-link>
            <span class="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-600">Beta</span>
          </div>

          <!-- Left Section: Logo and Navigation Links -->
          <div class="flex items-center space-x-4">
            <!-- Desktop Menu -->
            <div class="hidden lg:flex xl:space-x-5 space-x-1 items-center">
              <!-- Flight Search Dropdown -->
              <div
                class="relative"
                @mouseenter="openFlightSearch = true"
                @mouseleave="openFlightSearch = false"
              >
                <a
                  href="#"
                  class="flex items-center p-2 text-sm text-xs font-extrabold hover:text-gray-700 transition duration-300"
                  aria-haspopup="true"
                  :aria-expanded="openFlightSearch"
                >
                  <SunIcon class="h-4 w-4 mr-1" />
                  {{ t('nav.flightSearch') }}
                  <svg class="ml-1 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M19 9l-7 7-7-7" />
                  </svg>
                </a>
                <transition name="fade">
                  <div
                    v-if="openFlightSearch"
                    class="origin-top-left absolute left-0 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
                    role="menu"
                    aria-orientation="vertical"
                  >
                    <div class="py-1 text-sm">
                      <router-link data-tour="basic-search" to="/searches/basic" class="block px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors duration-300 group">
                        <div class="flex items-center">
                          <img :src="BasicSearchIcon" alt="Basic Search" class="h-4 w-4 mr-3 opacity-70 group-hover:opacity-100 transition-opacity" />
                          <div>
                            <span class="font-semibold group-hover:text-blue-600">{{ t('nav.basicSearch') }}</span>
                            <p class="text-xs text-gray-500 mt-0.5">{{ t('nav.basicSearchDesc') }}</p>
                          </div>
                        </div>
                      </router-link>
                      <router-link data-tour="buddy-search" to="/searches/buddy" class="block px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors duration-300 group">
                        <div class="flex items-center">
                          <img :src="BuddySearchIcon" alt="Buddy Search" class="h-4 w-4 mr-3 opacity-70 group-hover:opacity-100 transition-opacity" />
                          <div>
                            <span class="font-semibold group-hover:text-violet-600">{{ t('nav.buddySearch') }}</span>
                            <p class="text-xs text-gray-500 mt-0.5">{{ t('nav.buddySearchDesc') }}</p>
                          </div>
                        </div>
                      </router-link>
                      <router-link to="/searches/weather" class="relative block px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors duration-300 group">
                        <div class="flex items-center">
                          <SunIcon class="h-4 w-4 mr-3 opacity-70 group-hover:opacity-100 transition-opacity" />
                          <div>
                            <span class="font-semibold group-hover:text-amber-600">{{ t('nav.weatherSearch') }}</span>
                            <p class="text-xs text-gray-500 mt-0.5">{{ t('nav.weatherSearchDesc') }}</p>
                          </div>
                          <span class="ml-auto px-1 py-0.5 text-[8px] font-bold rounded bg-amber-500 text-white leading-none">NEW</span>
                        </div>
                      </router-link>
                    </div>
                  </div>
                </transition>
              </div>
              <div>
                <router-link data-tour="explore" to="/explore" class="flex items-center p-2 text-sm text-xs font-extrabold hover:text-gray-700 transition duration-300">
                  <GlobeEuropeAfricaIcon class="h-5 w-5 mr-2" />
                  {{ t('nav.explore') }}
                </router-link>
              </div>
              <!-- Dashboard Dropdown -->
              <div
                v-if="authStore.isAuthenticated"
                class="relative"
                @mouseenter="openDashboard = true"
                @mouseleave="openDashboard = false"
              >
                <a
                  href="#"
                  class="flex items-center p-2 text-sm text-xs font-extrabold hover:text-gray-700 transition duration-300"
                  aria-haspopup="true"
                  :aria-expanded="openDashboard"
                >
                  <Squares2X2Icon class="h-4 w-4 mr-1" />
                  {{ t('nav.dashboard') }}
                  <svg class="ml-1 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M19 9l-7 7-7-7" />
                  </svg>
                </a>
                <transition name="fade">
                  <div
                    v-if="openDashboard"
                    class="origin-top-left absolute left-0 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
                    role="menu"
                    aria-orientation="vertical"
                  >
                    <div class="py-2 px-3 text-sm">
                      <router-link to="/dashboard" class="block py-1.5 rounded-md text-gray-700 transition-colors duration-300 flex items-center group">
                        <Squares2X2Icon class="h-5 w-5 mr-3 ml-1 text-blue-500" />
                        <div class="flex flex-col">
                          <span class="font-semibold group-hover:text-blue-600">{{ t('nav.overview') }}</span>
                          <span class="text-xs text-gray-500 font-semibold group-hover:text-blue-500">{{ t('nav.overviewSub') }}</span>
                        </div>
                      </router-link>
                      <router-link to="/saved" class="block py-1.5 rounded-md text-gray-700 transition-colors duration-300 flex items-center group">
                        <BookmarkIcon class="h-5 w-5 mr-3 ml-1 text-blue-500" />
                        <span class="font-semibold group-hover:text-blue-600">{{ t('nav.savedFlights') }}</span>
                      </router-link>
                      <router-link to="/shared" class="block py-1.5 rounded-md text-gray-700 transition-colors duration-300 flex items-center group">
                        <ShareIcon class="h-5 w-5 mr-3 ml-1 text-violet-500" />
                        <span class="font-semibold group-hover:text-violet-600">{{ t('nav.collections') }}</span>
                      </router-link>
                      <router-link data-tour="price-alerts" to="/alerts" class="block py-1.5 rounded-md text-gray-700 transition-colors duration-300 flex items-center group">
                        <BellIcon class="h-5 w-5 mr-3 ml-1 text-violet-500" />
                        <span class="font-semibold group-hover:text-violet-600">{{ t('nav.alerts') }}</span>
                      </router-link>
                      <router-link to="/search-history" class="block py-1.5 rounded-md text-gray-700 transition-colors duration-300 flex items-center group">
                        <ClockIcon class="h-5 w-5 mr-3 ml-1 text-amber-500" />
                        <span class="font-semibold group-hover:text-amber-600">{{ t('nav.searchHistory') }}</span>
                      </router-link>
                      <router-link to="/search-settings" class="block py-1.5 rounded-md text-gray-700 transition-colors duration-300 flex items-center group">
                        <CogIcon class="h-5 w-5 mr-3 ml-1 text-slate-400" />
                        <span class="font-semibold group-hover:text-slate-600">{{ t('nav.settings') }}</span>
                      </router-link>
                      <router-link to="/visited" class="block py-1.5 rounded-md text-gray-700 transition-colors duration-300 flex items-center group">
                        <GlobeEuropeAfricaIcon class="h-5 w-5 mr-3 ml-1 text-slate-400" />
                        <span class="font-semibold group-hover:text-slate-600">{{ t('nav.visited') }}</span>
                      </router-link>
                      <router-link to="/planned" class="block py-1.5 rounded-md text-gray-700 transition-colors duration-300 flex items-center group">
                        <CalendarIcon class="h-5 w-5 mr-3 ml-1 text-amber-500" />
                        <span class="font-semibold group-hover:text-amber-600">{{ t('nav.planned') }}</span>
                      </router-link>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <div class="flex items-center sm:space-x-4 space-x-1">
            <!-- Login Button (Desktop) -->
            <router-link
              v-if="isOnResultsPage"
              to="/explore"
              class="lg:hidden rounded-xl bg-blue-600 px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-blue-700"
            >
              {{ t('nav.explore') }}
            </router-link>
            <div class="items-center space-x-4">
              <div v-if="!authStore.isAuthenticated" class="flex items-center space-x-2">
                <a
                  href="#"
                  @click.prevent="openLoginModal"
                  class="hidden lg:flex px-3 py-1 rounded-xl text-xs text-gray-700 hover:text-white font-bold bg-gray-200 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
                >
                  {{ t('nav.logIn') }}
                </a>
                <!-- Desktop CTA: UNLOCK MORE on search/explore pages, SEARCH elsewhere -->
                <a
                  v-if="isOnSearchPage || isOnExplorePage"
                  href="#"
                  data-tour="price-alerts"
                  @click.prevent="openSignupModal"
                  class="hidden lg:flex px-3 py-1 rounded-xl text-xs text-white font-bold bg-blue-600 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 shine-button"
                >
                  {{ t('nav.unlockMore') }}
                </a>
                <a
                  v-else
                  href="#"
                  @click.prevent="openSignupModal"
                  class="hidden lg:flex px-3 py-1 rounded-xl text-xs text-white font-bold bg-blue-600 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
                >
                  {{ t('nav.unlockMore') }}
                </a>
                <!-- Mobile CTA: UNLOCK MORE on search/explore pages (not landing), EXPLORE elsewhere -->
                <a
                  v-if="(isOnSearchPage && !isOnResultsPage && !isOnLandingPage) || isOnExplorePage"
                  href="#"
                  @click.prevent="openSignupModal"
                  class="lg:hidden px-3 py-1 rounded-xl text-xs text-white font-bold bg-blue-600 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 shine-button"
                >
                  {{ t('nav.unlockMore') }}
                </a>
                <router-link
                  v-else-if="!isOnResultsPage"
                  :to="localePath('/explore')"
                  class="lg:hidden px-3 py-1 rounded-xl text-xs text-white font-bold bg-blue-600 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
                >
                  {{ t('nav.explore') }}
                </router-link>
              </div>
              <div v-else class="flex items-center space-x-4 sm:space-x-2 text-sm">
                <a 
                  href="#"
                  @click.prevent="logout"
                  class="hidden lg:block sm:px-4 px-1 py-2 rounded-md text-center text-gray-700 hover:bg-blue-100 hover:text-blue-600 transition duration-300"
                >
                  {{ t('nav.logOut') }}
                </a>
              </div>
            </div>

            <!-- Language Switcher (desktop) -->
            <div class="hidden lg:flex items-center">
              <LanguageSwitcher />
            </div>

            <!-- Mobile Menu Button -->
            <div class="flex lg:hidden items-end">
              <button @click="toggleMobileMenu" class="focus:outline-none" :aria-label="t('nav.navigation')" :aria-expanded="mobileMenuOpen">
                <svg
                  class="h-7 w-7 transition-transform duration-300"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    v-if="!mobileMenuOpen"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                  <path
                    v-else
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Menu -->
      <transition name="fade">
        <div v-if="mobileMenuOpen" class="lg:hidden fixed inset-0 z-50 flex bg-slate-950/35 backdrop-blur-[1px]" @click.self="mobileMenuOpen = false">
          <div class="mobile-menu-panel ml-auto flex h-full w-[86vw] max-w-[360px] flex-col overflow-hidden rounded-l-2xl border-l border-slate-200 bg-white shadow-2xl">
          <!-- Top Section: Logo and Close Button -->
          <div class="border-b border-slate-200 bg-white px-5 py-4">
            <div class="flex items-center justify-between">
              <div>
                <router-link to="/" class="text-lg font-bold tracking-tight text-blue-800" @click="mobileMenuOpen = false">properflight</router-link>
                <p class="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{{ t('nav.navigation') }}</p>
              </div>
              <button @click="toggleMobileMenu" class="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700" aria-label="Close navigation">
                <XMarkIcon class="h-5 w-5" />
              </button>
            </div>
          </div>

          <!-- Middle Section: Scrollable Content -->
          <div class="flex-grow overflow-y-auto px-5 py-4">
            <!-- Search Section -->
            <div class="mb-5 animate-section" style="--delay: 0.3s">
              <h3 class="pb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{{ t('nav.flightSearch') }}</h3>
              <div class="divide-y divide-slate-100 border-y border-slate-100">
                <router-link data-tour="basic-search-mobile" to="/searches/basic" class="block px-1 py-3 text-slate-700 transition-colors hover:text-blue-700" @click="mobileMenuOpen = false">
                  <div class="font-semibold text-sm">{{ t('nav.basicSearch') }}</div>
                  <div class="text-xs text-gray-500 mt-0.5">{{ t('nav.basicSearchDesc') }}</div>
                </router-link>
                <router-link data-tour="buddy-search-mobile" to="/searches/buddy" class="block px-1 py-3 text-slate-700 transition-colors hover:text-blue-700" @click="mobileMenuOpen = false">
                  <div class="font-semibold text-sm">{{ t('nav.buddySearch') }}</div>
                  <div class="text-xs text-gray-500 mt-0.5">{{ t('nav.buddySearchDesc') }}</div>
                </router-link>
                <router-link to="/searches/weather" class="block px-1 py-3 text-slate-700 transition-colors hover:text-blue-700" @click="mobileMenuOpen = false">
                  <div class="flex items-center justify-between">
                    <div>
                      <div class="font-semibold text-sm flex items-center gap-1.5">
                        {{ t('nav.weatherSearch') }}
                        <span class="px-1 py-0.5 text-[9px] font-bold rounded bg-amber-500 text-white leading-none">NEW</span>
                      </div>
                      <div class="text-xs text-gray-500 mt-0.5">{{ t('nav.weatherSearchDesc') }}</div>
                    </div>
                  </div>
                </router-link>
              </div>
            </div>

            <!-- Explore Section -->
            <div class="mb-5 animate-section" style="--delay: 0.45s">
              <router-link data-tour="explore-mobile" to="/explore" class="flex items-center justify-between px-1 py-3 text-sm font-medium text-slate-700 transition-colors hover:text-blue-700" @click="mobileMenuOpen = false">
                <span class="text-slate-700">{{ t('nav.explore') }}</span>
                <span class="hidden">Browse destinations</span>
              </router-link>
            </div>

            <!-- Flight Dashboard Section -->
            <div class="animate-section" style="--delay: 0.6s">
              <h3 class="pb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{{ t('nav.yourFlights') }}</h3>
              <div class="divide-y divide-slate-100 border-y border-slate-100">
                <router-link v-if="authStore.isAuthenticated" to="/dashboard" class="flex items-center justify-between px-1 py-3 text-sm font-medium text-slate-700 transition-colors hover:text-blue-700" @click="mobileMenuOpen = false">
                  <span class="font-medium text-slate-700">{{ t('nav.dashboardOverview') }}</span>
                  <span class="hidden">All in one</span>
                </router-link>
                <router-link to="/saved" class="flex items-center justify-between px-1 py-3 text-sm font-medium text-slate-700 transition-colors hover:text-blue-700" @click="mobileMenuOpen = false">
                  <span class="text-slate-700">{{ t('nav.savedFlightsShort') }}</span>
                </router-link>
                <router-link to="/shared" class="flex items-center justify-between px-1 py-3 text-sm font-medium text-slate-700 transition-colors hover:text-blue-700" @click="mobileMenuOpen = false">
                  <span class="text-slate-700">{{ t('nav.collections') }}</span>
                </router-link>
                <router-link to="/alerts" class="flex items-center justify-between px-1 py-3 text-sm font-medium text-slate-700 transition-colors hover:text-blue-700" @click="mobileMenuOpen = false">
                  <span class="text-slate-700">{{ t('nav.alertResults') }}</span>
                </router-link>
                <router-link v-if="authStore.isAuthenticated" to="/search-history" class="flex items-center justify-between px-1 py-3 text-sm font-medium text-slate-700 transition-colors hover:text-blue-700" @click="mobileMenuOpen = false">
                  <span class="text-slate-700">{{ t('nav.searchHistoryShort') }}</span>
                </router-link>
                <router-link v-if="authStore.isAuthenticated" to="/search-settings" class="flex items-center justify-between px-1 py-3 text-sm font-medium text-slate-700 transition-colors hover:text-blue-700" @click="mobileMenuOpen = false">
                  <span class="text-slate-700">{{ t('nav.searchSettings') }}</span>
                </router-link>
                <router-link to="/visited" class="flex items-center justify-between px-1 py-3 text-sm font-medium text-slate-700 transition-colors hover:text-blue-700" @click="mobileMenuOpen = false">
                  <span class="text-slate-700">{{ t('nav.visitedShort') }}</span>
                </router-link>
                <router-link to="/planned" class="flex items-center justify-between px-1 py-3 text-sm font-medium text-slate-700 transition-colors hover:text-blue-700" @click="mobileMenuOpen = false">
                  <span class="text-slate-700">{{ t('nav.plannedShort') }}</span>
                </router-link>
              </div>
            </div>

            <!-- Language Switcher (mobile) -->
            <div class="mt-5 animate-section" style="--delay: 0.7s">
              <div class="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2.5">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
                <span class="text-sm font-medium text-slate-600">{{ t('nav.language') }}</span>
                <div class="ml-auto">
                  <LanguageSwitcher />
                </div>
              </div>
            </div>


          </div>

          <!-- Bottom Section: Authentication Buttons -->
          <div class="border-t border-slate-200 bg-white px-4 py-4 shadow-[0_-8px_20px_rgba(15,23,42,0.04)]">
            <div v-if="!authStore.isAuthenticated" class="flex space-x-3">
              <a
                href="#"
                @click.prevent="openLoginModal"
                class="flex-1 px-3 py-2 rounded-xl text-center text-gray-700 font-bold text-sm whitespace-nowrap bg-gray-200 hover:bg-gray-900 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-800 transition duration-300"
              >
                {{ t('nav.logIn') }}
              </a>
              <a
                href="#"
                @click.prevent="openSignupModal"
                class="flex-1 px-3 py-2 rounded-xl text-center text-white font-bold text-sm whitespace-nowrap bg-blue-600 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 shine-button"
              >
                {{ t('nav.unlockMore') }}
              </a>
            </div>
            <div v-else class="flex sm:space-x-4 space-x-1">
              <router-link
                to="/"
                class="flex-1 sm:px-4 px-2 py-2 rounded-md text-center text-gray-700 hover:bg-blue-100 hover:text-blue-600 transition duration-300"
              >
                {{ t('nav.searchMobile') }}
              </router-link>
              <a
                href="#"
                @click.prevent="logout"
                class="flex-1 sm:px-4 px-2 py-2 rounded-md text-center text-gray-700 hover:bg-blue-100 hover:text-blue-600 transition duration-300"
              >
                {{ t('nav.logOut') }}
              </a>
            </div>
          </div>
        </div>
        </div>
      </transition>
    </nav>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useRoute } from 'vue-router';
import { localePath } from '@/router';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n()

// Icons
import {
  GlobeEuropeAfricaIcon,
  CalendarIcon,
  BellIcon,
  ShareIcon,
  XMarkIcon,
  BookmarkIcon,
  Squares2X2Icon,
  ClockIcon,
  CogIcon,
  SunIcon
} from '@heroicons/vue/24/outline';
import BasicSearchIcon from '@/assets/icons/BasicSearchIcon2.png';
import BuddySearchIcon from '@/assets/icons/BuddySearchIcon2.png';

// Authentication store
const authStore = useAuthStore();
const route = useRoute();

// True when the user is on any search or results page
const isOnSearchPage = computed(() =>
  route.path === '/' || route.path.startsWith('/searches/') || route.path.startsWith('/search/')
);
const isOnResultsPage = computed(() => route.name === 'Results');
const isOnExplorePage = computed(() => route.path === '/explore');
const isOnLandingPage = computed(() => route.path === '/');

// Reactive state
const openSearch = ref(false);
const openDashboard = ref(false);
const openFlightSearch = ref(false);
const openCockpit = ref(false);
const mobileMenuOpen = ref(false);
const isCompact = ref(false);

// Function to toggle mobile menu
const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

// Authentication methods
const openLoginModal = () => {
  authStore.openAuthModal('login');
};

const openSignupModal = () => {
  authStore.openAuthModal('signup');
};

const logout = () => {
  authStore.logout();
};

// Track if the screen is mobile based on Tailwind's 'md' breakpoint (768px)
const isMobile = ref(window.innerWidth < 768);

// Update isMobile based on window resize
const updateIsMobile = () => {
  isMobile.value = window.innerWidth < 768;
};

// Navbar visibility based on scroll
const navbarVisible = ref(true);
let lastScrollY = 0;
let scrollContainer = null;

// Handler for scroll events
const handleScroll = () => {
  if (mobileMenuOpen.value) {
    // If mobile menu is open, keep navbar visible
    navbarVisible.value = true;
    return;
  }

  const currentScrollY = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
  isCompact.value = currentScrollY > 24;

  if (currentScrollY > lastScrollY && currentScrollY > 100) {
    // Scrolling down and past 100px, hide navbar
    navbarVisible.value = false;
  } else {
    // Scrolling up, show navbar
    navbarVisible.value = true;
  }

  lastScrollY = currentScrollY;
};

// Add event listeners on mount
onMounted(() => {
  scrollContainer = document.querySelector('.overflow-auto');
  lastScrollY = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
  isCompact.value = lastScrollY > 24;
  window.addEventListener('resize', updateIsMobile);
  (scrollContainer || window).addEventListener('scroll', handleScroll);
});

// Remove event listeners on unmount
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateIsMobile);
  (scrollContainer || window).removeEventListener('scroll', handleScroll);
});

// Watcher to close dropdowns when mobile menu is toggled
watch(mobileMenuOpen, (newVal) => {
  if (!newVal) {
    openSearch.value = false;
    openCockpit.value = false;
  }
});
</script>
<style>
/* Navbar transition */
.origin-top-right {
  top: 100%;
  right: 0;
}

.origin-top-left {
  top: 100%;
  left: 0;
}

/* Add fade transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.fade-enter-to, .fade-leave-from {
  opacity: 1;
}

/* Add slide transition for mobile menu */
.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-enter-to {
  transform: translateY(0);
  opacity: 1;
}

.slide-leave-from {
  transform: translateY(0);
  opacity: 1;
}

.slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.logo-container {
  position: relative;
}

.shine-button {
  position: relative;
  overflow: hidden;
}

.shine-button::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 150%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent,
    transparent 40%,
    rgba(255, 255, 255, 0.3) 40%,
    rgba(255, 255, 255, 0.2) 60%,
    transparent 60%,
    transparent 100%
  );
  transform: translateX(-100%);
  animation: shine-sweep 4s infinite;
}

@keyframes shine-sweep {
  0% {
    left: -100%;
  }
  20% {
    left: 200%;
  }
  100% {
    left: 200%;
  }
}

/* Ensure the mobile menu takes full height and uses flex layout */
.mobile-menu-panel {
  display: flex;
  flex-direction: column;
  height: 100vh; /* Full viewport height */
}

.mobile-menu-panel .divide-y > a::after {
  content: '›';
  color: #94a3b8;
  font-size: 1.25rem;
  font-weight: 300;
  line-height: 1;
}

.flex-grow {
  flex-grow: 1;
}

.overflow-y-auto {
  overflow-y: auto;
}

/* Optional: Add padding to the bottom to prevent content from being hidden behind the buttons */
.flex-grow.overflow-y-auto {
  padding-bottom: 60px; /* Adjust based on button height */
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-section {
  opacity: 0;
  transform: translateY(30px);
}

.mobile-menu-panel .animate-section {
  animation: slideIn 0.5s ease-out forwards;
  animation-delay: var(--delay);
}

/* Reset animation when menu is closed */
.fade-leave-active .animate-section {
  animation: none;
  opacity: 0;
  transform: translateY(30px);
}
</style>
